import React, { useEffect, useState } from "react";
import "./RegistraterForm.css";
import { useNewUser } from "../../../APIContext/NewUserContext";
import { useSummerCamp } from "../../../APIContext/summerCampContext";
import { Calendar } from "primereact/calendar";
import { PrimeReactProvider } from "primereact/api";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";

const SummerCampRegistrationForm = ({ offeringId, summercampList }) => {
  const [playerCount, setPlayerCount] = useState(1);
  const { loading, calculateSummerCampPrice, addToCartOffering } = useNewUser();
  const [price, setPrice] = useState(null);
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
  const [priceNeedsUpdate, setPriceNeedsUpdate] = useState(true);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [weekCount, setWeekCount] = useState(null);

  const [weekCapacityMap, setWeekCapacityMap] = useState({});

  const INITIAL_PLAYER = {
    internalId: 1,
    first_name: "",
    age: "",
    date_of_birth: "",
    grade: "",
    gender: "",
    jersey_size: "",
    school_name: "",
    week_ids: [],
    medical_condition: "",
    //maxWeeks: 0,
    maxWeeks: null, // ⭐ IMPORTANT
  };

  const INITIAL_PLAYERS = [INITIAL_PLAYER];
  const [players, setPlayers] = useState(INITIAL_PLAYERS);

  const { getOfferingCart } = useSummerCamp();

  const weeksOptions = summercampList?.find(
    (item) => String(item.id) === String(offeringId),
  )?.weeks;

  const jerseySizes = [
    { label: "Small", value: "S" },
    { label: "Medium", value: "M" },
    { label: "Large", value: "L" },
    { label: "Extra Large", value: "XL" },
    { label: "Youth Small", value: "YS" },
    { label: "Youth Medium", value: "YM" },
    { label: "Youth Large", value: "YL" },
  ];

  const gradeList = [
    { label: "2nd", value: "2nd" },
    { label: "3rd", value: "3rd" },
    { label: "4th", value: "4th" },
    { label: "5th", value: "5th" },
    { label: "6th", value: "6th" },
    { label: "7th", value: "7th" },
    { label: "8th", value: "8th" },
    { label: "High School", value: "High School" },
    { label: "College", value: "College" },
    { label: "Professional", value: "Professional" },
  ];

  const genderList = [
    { label: "Male", value: "male" },
    { label: "Female", value: "female" },
    { label: "Other", value: "other" },
  ];

  // Validate all form fields
  const validateForm = () => {
    const errors = [];

    players.forEach((player, index) => {
      const playerNum = index + 1;

      if (!player.first_name.trim()) {
        errors.push(`Player ${playerNum}: First name is required`);
      }
      if (!player.age) {
        errors.push(`Player ${playerNum}: Age is required`);
      }
      if (!player.date_of_birth) {
        errors.push(`Player ${playerNum}: Date of birth is required`);
      }
      if (!player.grade) {
        errors.push(`Player ${playerNum}: Grade is required`);
      }
      if (!player.gender) {
        errors.push(`Player ${playerNum}: Gender is required`);
      }
      if (!player.jersey_size) {
        errors.push(`Player ${playerNum}: Jersey size is required`);
      }
      if (!player.school_name.trim()) {
        errors.push(`Player ${playerNum}: School name is required`);
      }
      if (!player.medical_condition.trim()) {
        errors.push(`Player ${playerNum}: Medical condition is required`);
      }
      if (player.week_ids.length === 0) {
        errors.push(`Player ${playerNum}: At least one week must be selected`);
      }
    });

    return errors;
  };

  // Validate weeks only (for price calculation)
  const validateWeeksOnly = () => {
    const errors = [];

    players.forEach((player, index) => {
      if (player.week_ids.length === 0) {
        errors.push(`Player ${index + 1}: Please select at least one week`);
      }
    });

    return errors;
  };

  // Mark price as needing update whenever players or weeks change
  useEffect(() => {
    if (price !== null) {
      setPriceNeedsUpdate(true);
    }
  }, [players, playerCount]);

  useEffect(() => {
    const offering = summercampList?.find(
      (item) => String(item.id) === String(offeringId),
    );

    if (!offering) return;

    const capacityMap = {};

    offering.weeks.forEach((week) => {
      capacityMap[week.id] = week.remaining_capacity;
    });

    setWeekCapacityMap(capacityMap);
  }, [summercampList, offeringId]);

  const resetFormState = () => {
    setPlayerCount(1);
    setPlayers(INITIAL_PLAYERS);

    setPrice(null);
    setPriceNeedsUpdate(true);

    setValidationErrors([]);
    setIsCalculatingPrice(false);
    setIsAddingToCart(false);
  };

  // Handle fetching the price
  /* const handleGetPrice = async () => {
    // Validate weeks selection only for price calculation
    const weekErrors = validateWeeksOnly();

    if (weekErrors.length > 0) {
      setValidationErrors(weekErrors);
      return;
    }

    setValidationErrors([]);
    setIsCalculatingPrice(true);

    try {
      // Create weeks parameter: comma-separated count of weeks per player
      // Example: player1 has 2 weeks, player2 has 1 week, player3 has 2 weeks → "2,1,2"
      const weeksPerPlayer = players.map((player) => player.week_ids.length);
      const weeksParam = weeksPerPlayer.join(",");

      const result = await calculateSummerCampPrice({
        weeks: weeksParam,
      });

      if (result) {
        setPrice(result);
        setPriceNeedsUpdate(false);
      }
    } catch (error) {
      console.error("Error getting price:", error);
      setValidationErrors(["Failed to calculate price. Please try again."]);
    } finally {
      setIsCalculatingPrice(false);
    }
  }; */

  // Handle player count change
  const handlePlayerCountChange = (e) => {
    const newCount = parseInt(e.target.value, 10);
    setPlayerCount(newCount);

    setPlayers((prevPlayers) => {
      let updatedPlayers = [...prevPlayers];

      if (newCount > prevPlayers.length) {
        for (let i = prevPlayers.length; i < newCount; i++) {
          updatedPlayers.push({
            internalId: i + 1,
            first_name: "",
            age: "",
            date_of_birth: "",
            grade: "",
            gender: "",
            jersey_size: "",
            school_name: "",
            week_ids: [],
            medical_condition: "",
            maxWeeks: null,
          });
        }
      } else if (newCount < prevPlayers.length) {
        updatedPlayers = updatedPlayers.slice(0, newCount);
      }
      return updatedPlayers;
    });
  };

  // Handle input changes for a specific player
  const handlePlayerInputChange = (index, field, value) => {
    setPlayers((prevPlayers) => {
      const updatedPlayers = [...prevPlayers];
      updatedPlayers[index] = {
        ...updatedPlayers[index],
        [field]: value,
      };
      return updatedPlayers;
    });
  };

  // 🔥 AUTO SELECT ONLY WHEN 3 IS CHOSEN
  {
    /* const handleWeekLimitChange = (playerIndex, numWeeks) => {
    const limit = parseInt(numWeeks);
    if (isNaN(limit)) return;

    setPlayers((prev) => {
      const newPlayers = [...prev];
      const player = newPlayers[playerIndex];

      let updatedWeeks = player.week_ids;

      if (limit === 3) {
        updatedWeeks = weeksOptions.slice(0, 3).map((w) => Number(w.id));
      }

      if (updatedWeeks.length > limit) {
        updatedWeeks = updatedWeeks.slice(0, limit);
      }

      newPlayers[playerIndex] = {
        ...player,
        maxWeeks: limit,
        week_ids: updatedWeeks,
      };

      return newPlayers;
    });
  }; */
  }

  // const handleWeekLimitChange = (playerIndex, numWeeks) => {

  //   const limit = parseInt(numWeeks);
  //   if (isNaN(limit)) return;

  //   setPlayers((prev) => {
  //     const newPlayers = [...prev];
  //     const player = newPlayers[playerIndex];

  //     let updatedWeeks = [];

  //     // ⭐ Special rule: if 3 selected → auto-pick first 3 weeks
  //     if (limit === weeksOptions?.length) {
  //       updatedWeeks = weeksOptions.slice(0, weeksOptions?.length).map((w) => Number(w.id));
  //     }

  //     newPlayers[playerIndex] = {
  //       ...player,
  //       maxWeeks: limit,
  //       week_ids: updatedWeeks,
  //     };

  //     return newPlayers;
  //   });
  // };

  const handleWeekLimitChange = (playerIndex, numWeeks) => {
    const limit = parseInt(numWeeks);
    if (isNaN(limit)) return;

    setPlayers((prevPlayers) => {
      const newPlayers = [...prevPlayers];
      const player = newPlayers[playerIndex];

      const previousWeeks = player.week_ids || [];
      let newWeeks = [];

      // ⭐ Auto-select ONLY when full length is chosen
      if (limit === weeksOptions?.length) {
        newWeeks = weeksOptions.map((w) => Number(w.id));
      }

      // 🔁 SYNC CAPACITY
      setWeekCapacityMap((prev) => {
        const updated = { ...prev };

        // Restore capacity for old selections
        previousWeeks.forEach((weekId) => {
          updated[weekId] = (updated[weekId] ?? 0) + 1;
        });

        // Reduce capacity for new selections
        newWeeks.forEach((weekId) => {
          updated[weekId] = (updated[weekId] ?? 0) - 1;
        });

        return updated;
      });

      newPlayers[playerIndex] = {
        ...player,
        maxWeeks: limit,
        week_ids: newWeeks,
      };

      return newPlayers;
    });
  };

  const calculateAgeFromDOB = (dob) => {
    if (!dob) return "";

    const today = new Date();
    const birthDate = new Date(dob);

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  };

  // Handle date change for PrimeReact Calendar
  const handleDateChange = (index, e) => {
    const selectedDate = e.value;
    let formattedDate = "";

    const calculatedAge = calculateAgeFromDOB(selectedDate);

    if (selectedDate) {
      // Format date as YYYY-MM-DD for storage
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      formattedDate = `${year}-${month}-${day}`;

      setPlayers((prevPlayers) => {
        const updatedPlayers = [...prevPlayers];
        updatedPlayers[index] = {
          ...updatedPlayers[index],
          date_of_birth: formattedDate,
          age: calculatedAge,
        };
        return updatedPlayers;
      });
    }

    handlePlayerInputChange(index, "date_of_birth", formattedDate);
  };

  // const handleWeekToggle = (playerIndex, weekId) => {
  //   const idToToggle = Number(weekId);

  //   setPlayers((prev) => {
  //     const newPlayers = [...prev];
  //     const player = newPlayers[playerIndex];
  //     const currentIds = player.week_ids;
  //     const limit = player.maxWeeks;

  //     if (!limit) return prev;

  //     // If 3 weeks auto selected → allow uncheck but still respect max
  //     if (currentIds.includes(idToToggle)) {
  //       player.week_ids = currentIds.filter((id) => id !== idToToggle);
  //     } else {
  //       if (currentIds.length >= limit) return prev;
  //       player.week_ids = [...currentIds, idToToggle];
  //     }

  //     return newPlayers;
  //   });
  // };

  const handleWeekToggle = (playerIndex, weekId) => {
    const id = Number(weekId);

    setPlayers((prevPlayers) => {
      const playersCopy = [...prevPlayers];
      const player = playersCopy[playerIndex];

      if (!player.maxWeeks) return prevPlayers;

      const isSelected = player.week_ids.includes(id);

      // ❌ Prevent selecting if no capacity left
      if (!isSelected && weekCapacityMap[id] <= 0) {
        return prevPlayers;
      }

      let updatedWeeks;

      if (isSelected) {
        // ➕ deselect → restore capacity
        updatedWeeks = player.week_ids.filter((w) => w !== id);

        setWeekCapacityMap((prev) => ({
          ...prev,
          [id]: prev[id] + 1,
        }));
      } else {
        // ❌ limit reached
        if (player.week_ids.length >= player.maxWeeks) return prevPlayers;

        updatedWeeks = [...player.week_ids, id];

        setWeekCapacityMap((prev) => ({
          ...prev,
          [id]: prev[id] - 1,
        }));
      }

      playersCopy[playerIndex] = {
        ...player,
        week_ids: updatedWeeks,
      };

      return playersCopy;
    });
  };

  const handleAddToCart = async () => {
    // Validate all fields before adding to cart
    const errors = validateForm();

    if (errors.length > 0) {
      setValidationErrors(errors);
      // Scroll to top to show errors
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (!price || priceNeedsUpdate) {
      setValidationErrors([
        "Please get the updated price before adding to cart",
      ]);
      return;
    }

    setValidationErrors([]);
    setIsAddingToCart(true);

    try {
      // Prepare payload - remove internalId from each player
      const playersPayload = players.map(({ internalId, ...rest }) => rest);

      const payload = {
        offering_id: parseInt(offeringId),
        total_price: price.total_amount,
        players: playersPayload,
      };

      console.log("Submitting Payload:", payload);

      const result = await addToCartOffering(payload);

      if (result) {
        console.log("Successfully added to cart:", result);
        resetFormState();
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
      setValidationErrors(["Failed to add to cart. Please try again."]);
    } finally {
      setIsAddingToCart(false);
    }
  };

  const getSelectStyle = (value) => ({
    color: value ? "#555" : "#999",
  });

  // Determine button text and action
  /*const getButtonConfig = () => {
    if (price === null || priceNeedsUpdate) {
      return {
        text: price === null ? "Get Price" : "Get Updated Price",
        onClick: handleGetPrice,
        disabled: isCalculatingPrice,
      };
    }
    return {
      text: "Add To Cart",
      onClick: handleAddToCart,
      disabled: isAddingToCart,
    };
  }; */

  useEffect(() => {
    const autoCalculatePrice = async () => {
      // Check if at least one week selected for any player
      const hasWeeks = players.some((p) => p.week_ids.length > 0);
      if (!hasWeeks) {
        setPrice(null);
        return;
      }

      setIsCalculatingPrice(true);
      try {
        const weeksPerPlayer = players.map((p) => p.week_ids.length);
        const weeksParam = weeksPerPlayer.join(",");

        const result = await calculateSummerCampPrice({ weeks: weeksParam });

        if (result) {
          setPrice(result);
          setPriceNeedsUpdate(false);
        }
      } catch (err) {
        console.error("Auto price error", err);
      } finally {
        setIsCalculatingPrice(false);
      }
    };

    autoCalculatePrice();
  }, [players]); // 🔥 runs whenever weeks change

  //const buttonConfig = getButtonConfig();

  const primeReactConfig = {
    hideOverlaysOnDocumentScrolling: false,
  };

  const areWeeksValid = players.every(
    (p) => p.maxWeeks && p.week_ids.length === p.maxWeeks,
  );

  const weeksArray = [...Array(weeksOptions?.length || 0)].map((_, i) => i + 1);

  return (
    <PrimeReactProvider value={primeReactConfig}>
      <div
        style={{ filter: "brightness(1.28)" }}
        className="form-wrapper new-user bg-custom"
      >
        <div className="form-card main-card-smooth">
          <div className="form-title">SUMMER CAMP REGISTRATION</div>

          {/* Validation Errors Display */}
          {validationErrors.length > 0 && (
            <div
              style={{
                backgroundColor: "#fee",
                border: "1px solid #fcc",
                borderRadius: "4px",
                padding: "12px",
                marginBottom: "20px",
              }}
            >
              <strong style={{ color: "#c33" }}>
                Please fix the following errors:
              </strong>
              <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
                {validationErrors.map((error, idx) => (
                  <li key={idx} style={{ color: "#c33", marginBottom: "4px" }}>
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* --- Top Section: Player Count Control --- */}
          <div className="form-group">
            <label className="label-bold text-clip">How Many Players?*</label>
            <div className="top-row-controls">
              <select
                className="form-select smooth-input"
                value={playerCount}
                onChange={handlePlayerCountChange}
              >
                <option value={1}>1 Player</option>
                <option value={2}>2 Players</option>
                <option value={3}>3 Players</option>
                <option value={4}>4 Players</option>
                <option value={5}>5 Players</option>
              </select>
            </div>
          </div>

          {/* --- DYNAMIC PLAYER Rendering Section --- */}
          {players.map((player, index) => (
            <div key={player.internalId} className="player-section-grey">
              <div style={{ marginBottom: "1.2rem" }}>
                <label
                  className="label-bold headings text-clip"
                  style={{
                    fontSize: "1.1rem",
                    margin: 0,
                    "--clip-color": "#333",
                  }}
                >
                  Player {index + 1} Details
                </label>
              </div>

              <div className="summer-camp-grid">
                {/* Row 1: First Name & Age */}
                <div className="form-item-new">
                  <input
                    type="text"
                    className="form-input smooth-input"
                    placeholder="Full Name *"
                    value={player.first_name}
                    onChange={(e) =>
                      handlePlayerInputChange(
                        index,
                        "first_name",
                        e.target.value,
                      )
                    }
                  />
                </div>
                {/* Grade & Jersey Size */}
                <div className="form-item-new">
                  <select
                    className="form-select smooth-input"
                    value={player.gender}
                    onChange={(e) =>
                      handlePlayerInputChange(index, "gender", e.target.value)
                    }
                    // style={getSelectStyle(player.gender)}
                  >
                    <option value="" disabled hidden>
                      Gender
                    </option>
                    {genderList.map((item) => (
                      <option value={item.value} key={item.label}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date of Birth & School Name */}
                <div className="form-item-new">
                  <Calendar
                    placeholder="Date of Birth *"
                    value={
                      player.date_of_birth
                        ? new Date(player.date_of_birth)
                        : null
                    }
                    onChange={(e) => handleDateChange(index, e)}
                    showIcon
                    dateFormat="mm-dd-yy"
                    className="calesndr-cstm"
                    maxDate={new Date()}
                  />
                </div>

                <div className="form-item-new">
                  <input
                    type="number"
                    className="form-input smooth-input"
                    placeholder="Age *"
                    min="0"
                    value={player.age}
                    onChange={(e) =>
                      handlePlayerInputChange(index, "age", e.target.value)
                    }
                  />
                </div>

                <div className="form-item-new">
                  <select
                    className="form-select smooth-input"
                    value={player.grade}
                    onChange={(e) =>
                      handlePlayerInputChange(index, "grade", e.target.value)
                    }
                    // style={getSelectStyle(player.grade)}
                  >
                    <option value="" disabled hidden>
                      Grade*
                    </option>
                    {gradeList.map((item) => (
                      <option value={item.value} key={item.label}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-item-new">
                  <select
                    className="form-select smooth-input"
                    value={player.jersey_size}
                    onChange={(e) =>
                      handlePlayerInputChange(
                        index,
                        "jersey_size",
                        e.target.value,
                      )
                    }
                    style={getSelectStyle(player.jersey_size)}
                  >
                    <option value="" disabled hidden>
                      Jersey Size*
                    </option>
                    {jerseySizes.map((item) => (
                      <option key={item.label} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-item-new full-width-span">
                  <input
                    type="text"
                    className="form-input smooth-input"
                    placeholder="School Name *"
                    value={player.school_name}
                    onChange={(e) =>
                      handlePlayerInputChange(
                        index,
                        "school_name",
                        e.target.value,
                      )
                    }
                  />
                </div>

                {/*Medical Condition (Full Width) */}
                <div className="form-item-new full-width-span">
                  <textarea
                    className="form-input smooth-input"
                    style={{
                      height: "100px",
                      resize: "none",
                      fontFamily: "inherit",
                    }}
                    placeholder="Medical Condition*"
                    value={player.medical_condition}
                    onChange={(e) =>
                      handlePlayerInputChange(
                        index,
                        "medical_condition",
                        e.target.value,
                      )
                    }
                  ></textarea>

                  {/* dropdown to select week limit */}

                  <div
                    className="quick-select-wrapper"
                    style={{ marginTop: "15px" }}
                  >
                    <label
                      className="quick-select-label text-clip"
                      style={{ marginBottom: "10px" }}
                    >
                      How many weeks do you want to select?
                    </label>
                    <select
                      className="form-select smooth-input"
                      defaultValue=""
                      onChange={(e) =>
                        handleWeekLimitChange(index, e.target.value)
                      }
                    >
                      <option value="" disabled>
                        Choose number of week
                      </option>
                      {weeksArray.map((n) => (
                        <option key={n} value={n}>
                          {" "}
                          {n}
                        </option>
                      ))}
                    </select>

                    {player.maxWeeks && (
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#666",
                          marginTop: "5px",
                        }}
                      ></div>
                    )}
                  </div>

                  {player.maxWeeks && (
                    <div
                      className="quick-select-wrapper"
                      style={{
                        marginTop: "10px",
                        fontSize: "13px",
                        color:
                          player.week_ids.length === player.maxWeeks
                            ? "#2e7d32"
                            : "#c62828",
                      }}
                    >
                      {player.week_ids.length === player.maxWeeks ? (
                        <>
                          ✔ You have selected {player.maxWeeks} of{" "}
                          {player.maxWeeks} weeks
                        </>
                      ) : (
                        <>
                          Note: Please select {player.maxWeeks} weeks to
                          continue. You have selected {player.week_ids.length}{" "}
                          weeks.{" "}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/*  Weeks Checklist (Full Width) */}
                <div
                  className="form-item-new full-width-span"
                  style={{ marginTop: "10px" }}
                >
                  <label
                    className="label-bold text-clip"
                    style={{ marginBottom: "12px", display: "block" }}
                  >
                    Choose Summer Camp Weeks for Player {index + 1}*
                  </label>

                  <div className="big-checkbox-list">
                    {/* {weeksOptions?.map((weekObj) => {
                    const isChecked = player.week_ids.includes(weekObj.id);
                    const isDisabled = !player.maxWeeks; 

                    return (
                      <label
                        key={weekObj.id}
                        className={`big-checkbox-row ${isDisabled ? "disabled-week" : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={isDisabled}  
                          onChange={() => handleWeekToggle(index, weekObj.id)}
                          className="hidden-checkbox"
                        />
                        <div
                          className={`checkbox-box ${isChecked ? "checked" : ""}`}
                          style={{
                            opacity: isDisabled ? 0.4 : 1,
                            cursor: isDisabled ? "not-allowed" : "pointer",
                          }}
                        >
                          {isChecked && <span>✔</span>}
                        </div>
                        <span
                          className="checkbox-label-text"
                          style={{ opacity: isDisabled ? 0.5 : 1 }}
                        >
                          Week {weekObj?.week_number}
                        </span>
                      </label>
                    );
                  })} */}

                    {weeksOptions?.map((weekObj) => {
                      const isChecked = player.week_ids.includes(weekObj.id);
                      const isDisabled =
                        !player.maxWeeks ||
                        (!isChecked && weekCapacityMap[weekObj.id] === 0);

                      return (
                        <label
                          key={weekObj.id}
                          className={`big-checkbox-row ${isDisabled ? "disabled-week" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={isDisabled}
                            onChange={() => handleWeekToggle(index, weekObj.id)}
                            className="hidden-checkbox"
                          />

                          <div
                            className={`checkbox-box ${isChecked ? "checked" : ""}`}
                            style={{
                              opacity: isDisabled ? 0.4 : 1,
                              cursor: isDisabled ? "not-allowed" : "pointer",
                            }}
                          >
                            {isChecked && <span className="text-clip">✔</span>}
                          </div>

                          <span className="checkbox-label-text text-clip">
                            <span className="text-clip">
                              Week {weekObj.week_number}{" "}
                            </span>
                            {weekCapacityMap[weekObj.id] < 10 && (
                              <small
                                style={{ color: "#d01515", marginLeft: "6px" }}
                              >
                                (Only left {weekCapacityMap[weekObj.id]} )
                              </small>
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* --- Footer --- */}
          <div className="form-footer">
            {price !== null && !priceNeedsUpdate && (
              <div className="total-row">
                <span className="total-label text-clip">TOTAL</span>
                <span className="total-amount text-clip">
                  ${price.total_amount ? price.total_amount.toFixed(2) : "0.00"}
                </span>
              </div>
            )}

            {/*  <button
              className="add-cart-btn smooth-btn"
              onClick={buttonConfig.onClick}
              disabled={buttonConfig.disabled}
            >
              {isCalculatingPrice
                ? "Calculating..."
                : isAddingToCart
                  ? "Adding to Cart..."
                  : buttonConfig.text}
            </button> */}
            <button
              className="add-cart-btn smooth-btn"
              onClick={handleAddToCart}
              disabled={
                !price || isAddingToCart || isCalculatingPrice || !areWeeksValid
              }
            >
              {isCalculatingPrice
                ? "Calculating..."
                : isAddingToCart
                  ? "Adding to Cart..."
                  : "Add To Cart"}
            </button>
          </div>
        </div>
      </div>
    </PrimeReactProvider>
  );
};

export default SummerCampRegistrationForm;
